import React from 'react';
import { Card, Badge, Alert } from 'react-bootstrap';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('vi-VN').format(amount || 0) + 'đ';
};

const DISCREPANCY_LABELS = {
  MISSING_VOUCHER: 'Thiếu phiếu chi',
  UNRECORDED_ORDER: 'Đơn chưa ghi nhận',
  INPUT_ERROR: 'Lỗi nhập liệu',
  THEFT_OR_LOSS: 'Mất mát',
  SYSTEM_ERROR: 'Lỗi hệ thống',
  CASH_FLOAT_ADJUSTMENT: 'Điều chỉnh quỹ',
  OTHER: 'Khác',
};

const CashReportTab = ({ reconciliation, shift }) => {
  if (!reconciliation && !shift?.is_cashier) {
    return (
      <Alert variant='info'>
        Ca này không phải ca thu ngân, không có báo cáo tiền mặt.
      </Alert>
    );
  }

  const openingCash = Number(shift?.opening_cash || reconciliation?.opening_cash || 0);
  const closingCash = Number(shift?.closing_cash || reconciliation?.actual_closing || 0);
  const cashRevenue = reconciliation?.cash_revenue || 0;
  const cashExpenses = reconciliation?.cash_expenses || 0;
  const expectedClosing = reconciliation?.expected_closing || (openingCash + cashRevenue - cashExpenses);
  const difference = reconciliation?.difference || (closingCash - expectedClosing);

  const isReconciled = reconciliation?.is_reconciled;
  const hasDiscrepancy = Math.abs(difference) >= 1000;

  return (
    <>
      {/* Cash Flow Visual */}
      <Card className='shadow-sm border-0 mb-4'>
        <Card.Header className='bg-white fw-bold border-bottom'>
          <i className='icofont-chart-flow me-2 text-primary'></i>
          Dòng tiền mặt
        </Card.Header>
        <Card.Body>
          <div className='d-flex flex-wrap align-items-center justify-content-center gap-2 py-3'>
            {/* Opening */}
            <div className='text-center px-3'>
              <div className='text-muted small mb-1'>Đầu ca</div>
              <div className='fw-bold fs-4'>{formatCurrency(openingCash)}</div>
            </div>

            <div className='text-success fs-3 d-none d-md-block'>
              <i className='icofont-long-arrow-right'></i>
            </div>

            {/* Revenue */}
            <div className='text-center px-3'>
              <div className='text-muted small mb-1'>+ Doanh thu TM</div>
              <div className='fw-bold fs-4 text-success'>+{formatCurrency(cashRevenue)}</div>
            </div>

            <div className='text-danger fs-3 d-none d-md-block'>
              <i className='icofont-long-arrow-right'></i>
            </div>

            {/* Expenses */}
            <div className='text-center px-3'>
              <div className='text-muted small mb-1'>- Phiếu chi</div>
              <div className='fw-bold fs-4 text-danger'>-{formatCurrency(cashExpenses)}</div>
            </div>

            <div className='text-primary fs-3 d-none d-md-block'>
              <i className='icofont-long-arrow-right'></i>
            </div>

            {/* Expected */}
            <div className='text-center px-3'>
              <div className='text-muted small mb-1'>= Dự kiến</div>
              <div className='fw-bold fs-4'>{formatCurrency(expectedClosing)}</div>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Actual vs Expected */}
      <div className='row g-3 mb-4'>
        <div className='col-md-4'>
          <Card className='shadow-sm border-0 h-100'>
            <Card.Body className='text-center'>
              <div className='text-muted small mb-1'>Cuối ca (dự kiến)</div>
              <div className='fw-bold fs-4'>{formatCurrency(expectedClosing)}</div>
            </Card.Body>
          </Card>
        </div>
        <div className='col-md-4'>
          <Card className='shadow-sm border-0 h-100'>
            <Card.Body className='text-center'>
              <div className='text-muted small mb-1'>Cuối ca (thực tế)</div>
              <div className='fw-bold fs-4'>{formatCurrency(closingCash)}</div>
            </Card.Body>
          </Card>
        </div>
        <div className='col-md-4'>
          <Card className={`shadow-sm border-0 h-100 ${hasDiscrepancy ? 'border-danger border-2' : ''}`}>
            <Card.Body className='text-center'>
              <div className='text-muted small mb-1'>Chênh lệch</div>
              <div className={`fw-bold fs-4 ${difference > 0 ? 'text-success' : difference < 0 ? 'text-danger' : ''}`}>
                {difference > 0 ? '+' : ''}{formatCurrency(difference)}
              </div>
              {hasDiscrepancy && (
                <Badge bg='danger' className='mt-1'>
                  <i className='icofont-warning me-1'></i>
                  Cần kiểm tra
                </Badge>
              )}
            </Card.Body>
          </Card>
        </div>
      </div>

      {/* Reconciliation Status */}
      <Card className='shadow-sm border-0 mb-4'>
        <Card.Header className='bg-white fw-bold'>
          <i className='icofont-check-alt me-2 text-primary'></i>
          Trạng thái đối soát
        </Card.Header>
        <Card.Body>
          <div className='d-flex align-items-center gap-3 mb-3'>
            <Badge bg={isReconciled ? 'success' : hasDiscrepancy ? 'danger' : 'warning'} className='py-2 px-3'>
              {isReconciled ? 'Đã đối soát' : hasDiscrepancy ? 'Chênh lệch cần xử lý' : 'Chưa đối soát'}
            </Badge>
            {reconciliation?.reconciled_by && (
              <span className='text-muted small'>
                bởi {reconciliation.reconciled_by.full_name}
                {reconciliation.reconciled_at && ` lúc ${new Date(reconciliation.reconciled_at).toLocaleString('vi-VN')}`}
              </span>
            )}
          </div>

          {!hasDiscrepancy && !isReconciled && (
            <Alert variant='success' className='mb-0'>
              <i className='icofont-check-circled me-1'></i>
              Chênh lệch dưới 1,000đ — tự động khớp số.
            </Alert>
          )}

          {hasDiscrepancy && (
            <div className='row g-3'>
              <div className='col-md-4'>
                <div className='small text-muted mb-1'>Loại chênh lệch</div>
                <div className='small fw-medium'>
                  {DISCREPANCY_LABELS[reconciliation?.discrepancy_type] || reconciliation?.discrepancy_type || '—'}
                </div>
              </div>
              <div className='col-md-4'>
                <div className='small text-muted mb-1'>Giải thích</div>
                <div className='small'>{reconciliation?.explanation || '—'}</div>
              </div>
              <div className='col-md-4'>
                <div className='small text-muted mb-1'>Bằng chứng</div>
                {reconciliation?.evidence_urls?.length > 0 ? (
                  <div className='d-flex gap-2'>
                    {reconciliation.evidence_urls.map((url, i) => (
                      <a key={i} href={url} target='_blank' rel='noopener noreferrer'>
                        <img src={url} alt='' className='rounded border' style={{ width: 60, height: 45, objectFit: 'cover' }} />
                      </a>
                    ))}
                  </div>
                ) : (
                  <span className='small text-muted'>—</span>
                )}
              </div>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Formula */}
      <Card className='shadow-sm border-0'>
        <Card.Body className='text-center py-3'>
          <div className='text-muted small'>
            <strong>Công thức:</strong> Cuối ca dự kiến = Đầu ca + Doanh thu tiền mặt - Phiếu chi
          </div>
          <div className='small text-muted mt-1'>
            {formatCurrency(expectedClosing)} = {formatCurrency(openingCash)} + {formatCurrency(cashRevenue)} - {formatCurrency(cashExpenses)}
          </div>
        </Card.Body>
      </Card>
    </>
  );
};

export default CashReportTab;
